import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { TextInput } from "./Field";
import { FilterChip, useCloseOnOutside } from "./FilterMenu";
import { formatDate } from "../data/format";

/** A preset: how many days back from today the range starts (0 = today only). */
const PRESETS: { label: string; days: number }[] = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

/** YYYY-MM-DD for today minus `days`, in local time. */
export function daysAgoIso(days: number, now = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}


/** The chip text for a range: a preset's name when it matches one, else the dates. */
export function describeRange(from: string, to: string, now = new Date()): string {
  const today = daysAgoIso(0, now);
  if (to === today) {
    const preset = PRESETS.find((p) => daysAgoIso(p.days, now) === from);
    if (preset) return preset.label;
  }
  if (from && to) return from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `from ${formatDate(from)}`;
  return `until ${formatDate(to)}`;
}

/**
 * A filter chip for a date range: presets for the common asks, plus a custom
 * From / To. Same look and behaviour as FilterMenu — dashed until set, solid
 * with the value and a clear control once set, closes on outside click and
 * Escape. `from`/`to` are YYYY-MM-DD or empty; `onChange` gets both at once
 * so the URL updates in one step.
 */
export function DateRangeFilter({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const ref = useRef<HTMLDivElement>(null);
  const set = from !== "" || to !== "";

  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, to, open]);

  useCloseOnOutside(ref, open, () => setOpen(false));

  const apply = (range: { from: string; to: string }) => {
    onChange(range);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <FilterChip label={label} set={set} open={open} popup="dialog" onToggle={() => setOpen((o) => !o)} onClear={() => apply({ from: "", to: "" })}>
        {describeRange(from, to)}
      </FilterChip>

      {open && (
        <div role="dialog" aria-label={`${label} date range`} className="absolute z-20 mt-1 w-64 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-1 shadow-lg">
          {PRESETS.map((p) => {
            const range = { from: daysAgoIso(p.days), to: daysAgoIso(0) };
            const active = range.from === from && range.to === to;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => apply(range)}
                className={[
                  "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-gray-100 focus-visible:focus-ring",
                  active ? "font-medium text-[var(--text)]" : "text-gray-700",
                ].join(" ")}
              >
                {p.label}
                {active && <Icon name="check" className="text-brand-600" />}
              </button>
            );
          })}
          <div className="mt-1 grid gap-2 border-t border-[var(--border)] p-2">
            <label className="grid gap-1 text-xs text-[var(--text-2)]">
              From
              <TextInput type="date" aria-label={`${label} from`} value={draftFrom} max={draftTo || undefined} onChange={(e) => setDraftFrom(e.target.value)} />
            </label>
            <label className="grid gap-1 text-xs text-[var(--text-2)]">
              To
              <TextInput type="date" aria-label={`${label} to`} value={draftTo} min={draftFrom || undefined} onChange={(e) => setDraftTo(e.target.value)} />
            </label>
            <button
              type="button"
              disabled={draftFrom === "" && draftTo === ""}
              onClick={() => apply({ from: draftFrom, to: draftTo })}
              className="rounded-md bg-brand-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
